import React, { useState, useMemo } from 'react';
import { buildingStyles as defaultBuildingStyles, buildingStyleProfiles as defaultProfiles, installationSurfaces, roofStyles as defaultRoofStyles, colors as defaultColors, dimensions } from '../data/configuratorMock';
import { ChevronUp, ChevronDown, Heart, X, Move } from 'lucide-react';

// Helper function to get color name from hex value
const getColorNameFromHex = (hex, colorList) => {
  if (!hex || hex === 'none') return 'None';
  const found = colorList.find(c => c.value?.toLowerCase() === hex?.toLowerCase());
  return found?.name || hex;
};

const AccordionSection = ({ title, isOpen, onToggle, children, badge }) => (
  <div className="border-b border-gray-200 dark:border-gray-700 dark:border-gray-700">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-4 px-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
    >
      <span className="text-sm font-bold tracking-wide text-gray-800 dark:text-gray-200 flex items-center gap-2">
        {title}
        {badge > 0 && (
          <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">{badge}</span>
        )}
      </span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
      )}
    </button>
    {isOpen && (
      <div className="px-4 pb-4">
        {children}
      </div>
    )}
  </div>
);

const ConfiguratorSidebar = ({ 
  config, 
  activeTab, 
  setActiveTab, 
  onConfigChange, 
  onAddGarageDoor,
  onAddWalkDoor,
  onAddWindow,
  onRemoveItem,
  onUpdateItemPosition,
  onGetQuote, 
  currentProfile,
  canSeePricing,
  buildingEstimate,
  configuratorSettings
}) => {
  // Use settings from backend or fallback to defaults
  const colors = useMemo(() => {
    if (configuratorSettings?.colors) {
      // Backend returns flat array with category field, convert to grouped format
      const colorArray = configuratorSettings.colors;
      return {
        roof: colorArray.filter(c => c.category === 'roof'),
        trim: colorArray.filter(c => c.category === 'trim'),
        sides: colorArray.filter(c => c.category === 'sides'),
        wainscot: colorArray.filter(c => c.category === 'wainscot')
      };
    }
    return defaultColors;
  }, [configuratorSettings]);
  
  const roofStyles = useMemo(() => {
    if (configuratorSettings?.roof_styles) {
      return configuratorSettings.roof_styles;
    }
    return defaultRoofStyles;
  }, [configuratorSettings]);
  
  const buildingStyles = useMemo(() => {
    if (configuratorSettings?.building_styles) {
      return configuratorSettings.building_styles;
    }
    return defaultBuildingStyles;
  }, [configuratorSettings]);
  
  const buildingStyleProfiles = useMemo(() => {
    if (configuratorSettings?.building_styles) {
      const profiles = {};
      configuratorSettings.building_styles.forEach(style => {
        profiles[style.id] = {
          id: style.id,
          name: style.name,
          description: style.description || '',
          defaults: style.defaults || defaultProfiles[style.id]?.defaults || defaultProfiles.carport.defaults
        };
      });
      return profiles;
    }
    return defaultProfiles;
  }, [configuratorSettings]);
  
  const allColors = useMemo(() => {
    return [...(colors.roof || []), ...(colors.trim || []), ...(colors.sides || []), ...(colors.wainscot || [])];
  }, [colors]);
  
  // Local helper that uses allColors from this component
  const getColorName = (hex) => getColorNameFromHex(hex, allColors);

  const [openSections, setOpenSections] = useState({
    style: true,
    size: false,
    colors: false,
    ends: false,  // Now combined "ENCLOSURES" section
    frameSiding: false,
    doors: false,
    windows: false,
    services: false,
    structureDetails: false,
    buildingEstimate: false,
    extras: false,
  });
  
  const [selectedWall, setSelectedWall] = useState('leftEnd');

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Get current style info
  const currentStyle = buildingStyles.find(s => s.id === config.style);
  const profile = currentProfile || buildingStyleProfiles.carport;

  // Get enclosed walls for door/window placement
  const enclosedWalls = useMemo(() => {
    const walls = [];
    if (config.leftEnd === 'enclosed') walls.push({ id: 'leftEnd', name: 'Left End (Front)' });
    if (config.rightEnd === 'enclosed') walls.push({ id: 'rightEnd', name: 'Right End (Back)' });
    if (config.frontSide === 'enclosed') walls.push({ id: 'frontSide', name: 'Front Side' });
    if (config.backSide === 'enclosed') walls.push({ id: 'backSide', name: 'Back Side' });
    return walls;
  }, [config.leftEnd, config.rightEnd, config.frontSide, config.backSide]);

  // Filter dimensions based on profile constraints
  const availableWidths = useMemo(() => {
    return dimensions.widths.filter(w => 
      w >= (profile.widthRange?.min || 12) && 
      w <= (profile.widthRange?.max || 50)
    );
  }, [profile]);

  const availableLengths = useMemo(() => {
    return dimensions.lengths.filter(l => 
      l >= (profile.lengthRange?.min || 21) && 
      l <= (profile.lengthRange?.max || 101)
    );
  }, [profile]);

  const availableHeights = useMemo(() => {
    return dimensions.legHeights.filter(h => 
      h >= (profile.heightRange?.min || 6) && 
      h <= (profile.heightRange?.max || 16)
    );
  }, [profile]);

  // Door and window options
  const garageDoorOptions = [
    { id: '10x8', name: 'Garage Door 10x8' },
    { id: '9x7', name: 'Garage Door 9x7' },
    { id: '12x10', name: 'Garage Door 12x10' },
    { id: '8x8', name: 'Roll-Up Door 8x8' },
    { id: '10x10', name: 'Roll-Up Door 10x10' },
  ];

  const windowOptions = [
    { id: '30x30', name: 'Window 30"x30"' },
    { id: '36x36', name: 'Window 36"x36"' },
    { id: '24x36', name: 'Window 24"x36"' },
    { id: '48x36', name: 'Window 48"x36"' },
  ];

  return (
    <div className="w-96 bg-white dark:bg-gray-900 shadow-lg dark:shadow-gray-950/50 flex flex-col border-l border-gray-200 dark:border-gray-700 dark:border-gray-800 h-full max-h-full overflow-hidden transition-colors duration-300">
      {/* Quote Button */}
      <div className="p-4 flex-shrink-0">
        <button
          onClick={onGetQuote}
          className="w-full py-4 bg-red-600 text-white text-lg font-bold rounded hover:bg-red-700 transition-colors"
        >
          QUOTE
        </button>
      </div>

      {/* Building Type Title */}
      <div className="px-4 pb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{currentStyle?.name || 'Carport'}</h2>
          <button className="flex items-center gap-1 text-red-600 hover:text-red-700 text-sm">
            <Heart className="w-4 h-4" />
            <span>Save</span>
          </button>
        </div>
        {profile.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{profile.description}</p>
        )}
      </div>

      {/* Scrollable Accordion Area */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {/* STYLE Section */}
        <AccordionSection
          title="STYLE"
          isOpen={openSections.style}
          onToggle={() => toggleSection('style')}
        >
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Select a style below to get started. Each style comes with optimized default settings.
          </p>
          <div className="grid grid-cols-3 gap-3">
            {buildingStyles.map((style) => (
              <button
                key={style.id}
                onClick={() => onConfigChange('style', style.id)}
                className={`flex flex-col items-center p-2 rounded border-2 transition-all ${
                  config.style === style.id
                    ? 'border-red-600 bg-red-50 dark:bg-red-900/30'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="w-full h-12 bg-gray-100 dark:bg-gray-800 rounded mb-1 flex items-center justify-center">
                  <StyleIcon styleId={style.id} isSelected={config.style === style.id} />
                </div>
                <span className="text-xs text-gray-700 dark:text-gray-200 text-center leading-tight">{style.name}</span>
              </button>
            ))}
          </div>

          <div className="mt-6">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              <strong>Installation Surface</strong>
            </p>
            <div className="space-y-2">
              {installationSurfaces.map((surface) => (
                <label key={surface.id} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="installationSurface"
                    checked={config.installationSurface === surface.id}
                    onChange={() => onConfigChange('installationSurface', surface.id)}
                    className="w-4 h-4 text-red-600 border-gray-300 dark:border-gray-600 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">{surface.name}</span>
                </label>
              ))}
            </div>
          </div>
        </AccordionSection>

        {/* SIZE Section */}
        <AccordionSection
          title="SIZE"
          isOpen={openSections.size}
          onToggle={() => toggleSection('size')}
        >
          <p className="text-xs text-gray-500 mb-4">
            Dimensions for {currentStyle?.name}: {profile.widthRange?.min}'-{profile.widthRange?.max}' W
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Width</label>
              <select
                value={config.width}
                onChange={(e) => onConfigChange('width', parseInt(e.target.value))}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                {availableWidths.map((w) => (
                  <option key={w} value={w}>{w}'</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Length</label>
              <select
                value={config.length}
                onChange={(e) => onConfigChange('length', parseInt(e.target.value))}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                {availableLengths.map((l) => (
                  <option key={l} value={l}>{l}'</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Leg Height</label>
              <select
                value={config.legHeight}
                onChange={(e) => onConfigChange('legHeight', parseInt(e.target.value))}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                {availableHeights.map((h) => (
                  <option key={h} value={h}>{h}'</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Roof Style</label>
              <select
                value={config.roofStyle}
                onChange={(e) => onConfigChange('roofStyle', e.target.value)}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                {roofStyles.map((style) => (
                  <option key={style.id} value={style.id}>{style.name}</option>
                ))}
              </select>
            </div>
          </div>
        </AccordionSection>

        {/* COLORS Section */}
        <AccordionSection
          title="COLORS"
          isOpen={openSections.colors}
          onToggle={() => toggleSection('colors')}
        >
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Roof Color</label>
              <div className="grid grid-cols-5 gap-2">
                {(colors.roof || []).map((color, index) => (
                  <button
                    key={color.value}
                    data-testid={`roof-color-${index}`}
                    onClick={() => onConfigChange('roofColor', color.value)}
                    className={`w-10 h-10 rounded-full border-2 transition-all ${
                      config.roofColor === color.value
                        ? 'border-green-500 ring-2 ring-green-300'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Trim Color</label>
              <div className="grid grid-cols-5 gap-2">
                {(colors.trim || []).map((color, index) => (
                  <button
                    key={color.value}
                    data-testid={`trim-color-${index}`}
                    onClick={() => onConfigChange('trimColor', color.value)}
                    className={`w-10 h-10 rounded-full border-2 transition-all ${
                      config.trimColor === color.value
                        ? 'border-green-500 ring-2 ring-green-300'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Side/Wall Color</label>
              <div className="grid grid-cols-5 gap-2">
                {(colors.sides || []).map((color, index) => (
                  <button
                    key={color.value}
                    data-testid={`side-color-${index}`}
                    onClick={() => onConfigChange('sideColor', color.value)}
                    className={`w-10 h-10 rounded-full border-2 transition-all ${
                      config.sideColor === color.value
                        ? 'border-green-500 ring-2 ring-green-300'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Wainscot Color</label>
              <p className="text-xs text-gray-500 mb-2">36&quot; decorative panel on lower walls</p>
              <div className="grid grid-cols-5 gap-2">
                {(colors.wainscot || []).map((color, index) => (
                  <button
                    key={color.value}
                    data-testid={`wainscot-color-${index}`}
                    onClick={() => onConfigChange('wainscotColor', color.value)}
                    className={`w-10 h-10 rounded-full border-2 transition-all ${
                      config.wainscotColor === color.value
                        ? 'border-green-500 ring-2 ring-green-300'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                    } ${color.value === 'none' ? 'bg-gray-100 dark:bg-gray-800 flex items-center justify-center' : ''}`}
                    style={{ backgroundColor: color.value !== 'none' ? color.value : undefined }}
                    title={color.name}
                  >
                    {color.value === 'none' && <X className="w-4 h-4 text-gray-400" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </AccordionSection>

        {/* SIDES AND ENDS Section - Combined */}
        <AccordionSection
          title="SIDES AND ENDS"
          isOpen={openSections.ends}
          onToggle={() => toggleSection('ends')}
        >
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Configure wall enclosures for your building.
          </p>
          
          {/* Ends */}
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Ends</h4>
            <div className="grid grid-cols-2 gap-3">
              {['leftEnd', 'rightEnd'].map((end) => (
                <div key={end}>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                    {end === 'leftEnd' ? 'Front Gable End' : 'Back Gable End'}
                  </label>
                  <select
                    value={config[end]}
                    onChange={(e) => onConfigChange(end, e.target.value)}
                    className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="open">Open</option>
                    <option value="enclosed">Enclosed</option>
                    <option value="gable">Gable End</option>
                    <option value="extended">Extended Gable End</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
          
          {/* Sides */}
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Sides</h4>
            <div className="grid grid-cols-2 gap-3">
              {['frontSide', 'backSide'].map((side) => (
                <div key={side}>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                    {side === 'frontSide' ? 'Left Side' : 'Right Side'}
                  </label>
                  <select
                    value={config[side]}
                    onChange={(e) => onConfigChange(side, e.target.value)}
                    className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="open">Open</option>
                    <option value="enclosed">Enclosed</option>
                    <option value="top-1.5">Top - 1 1/2' Panel</option>
                    <option value="top-3">Top - 3' Panel</option>
                    <option value="top-6">Top - 6' Panel</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
          
          {/* Lean-Tos - Hidden for carport template */}
          {config.style !== 'carport' && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Lean-Tos</h4>
            <div className="grid grid-cols-2 gap-3">
              {/* Left Side Lean */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-2">Left Side Lean</label>
                <div className="space-y-1">
                  {[
                    { value: 'none', label: 'None' },
                    { value: 'lean_only', label: 'Lean Only' },
                    { value: 'lean_storage', label: 'Lean w/ Storage' }
                  ].map((option) => (
                    <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="leftLean"
                        value={option.value}
                        checked={config.leftLean === option.value || (!config.leftLean && option.value === 'none')}
                        onChange={(e) => onConfigChange('leftLean', e.target.value)}
                        className="w-3 h-3 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-xs text-gray-700 dark:text-gray-200">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Right Side Lean */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-2">Right Side Lean</label>
                <div className="space-y-1">
                  {[
                    { value: 'none', label: 'None' },
                    { value: 'lean_only', label: 'Lean Only' },
                    { value: 'lean_storage', label: 'Lean w/ Storage' }
                  ].map((option) => (
                    <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="rightLean"
                        value={option.value}
                        checked={config.rightLean === option.value || (!config.rightLean && option.value === 'none')}
                        onChange={(e) => onConfigChange('rightLean', e.target.value)}
                        className="w-3 h-3 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-xs text-gray-700 dark:text-gray-200">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Left Lean-To Dimensions - show when left lean is selected */}
              {config.leftLean && config.leftLean !== 'none' && (
                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-3">Left Lean-To Dimensions</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">Width (ft)</label>
                      <select
                        value={config.leftLeanWidth || 10}
                        onChange={(e) => onConfigChange('leftLeanWidth', parseInt(e.target.value))}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {(configuratorSettings?.leanToWidths || [8, 10, 12, 14, 16]).map((w) => (
                          <option key={w} value={w}>{w}'</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">Length (ft)</label>
                      <select
                        value={config.leftLeanLength || config.length || 21}
                        onChange={(e) => onConfigChange('leftLeanLength', parseInt(e.target.value))}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {(configuratorSettings?.leanToLengths || [21, 26, 30, 31, 36, 41, 46, 51]).map((len) => (
                          <option key={len} value={len}>{len}'</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">Leg Height (ft)</label>
                      <select
                        value={config.leftLeanHeight || 8}
                        onChange={(e) => onConfigChange('leftLeanHeight', parseInt(e.target.value))}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {(configuratorSettings?.leanToHeights || [6, 7, 8, 9, 10, 11, 12]).map((ht) => (
                          <option key={ht} value={ht}>{ht}'</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Right Lean-To Dimensions - show when right lean is selected */}
              {config.rightLean && config.rightLean !== 'none' && (
                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-3">Right Lean-To Dimensions</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">Width (ft)</label>
                      <select
                        value={config.rightLeanWidth || 10}
                        onChange={(e) => onConfigChange('rightLeanWidth', parseInt(e.target.value))}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {(configuratorSettings?.leanToWidths || [8, 10, 12, 14, 16]).map((w) => (
                          <option key={w} value={w}>{w}'</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">Length (ft)</label>
                      <select
                        value={config.rightLeanLength || config.length || 21}
                        onChange={(e) => onConfigChange('rightLeanLength', parseInt(e.target.value))}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {(configuratorSettings?.leanToLengths || [21, 26, 30, 31, 36, 41, 46, 51]).map((len) => (
                          <option key={len} value={len}>{len}'</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">Leg Height (ft)</label>
                      <select
                        value={config.rightLeanHeight || 8}
                        onChange={(e) => onConfigChange('rightLeanHeight', parseInt(e.target.value))}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {(configuratorSettings?.leanToHeights || [6, 7, 8, 9, 10, 11, 12]).map((ht) => (
                          <option key={ht} value={ht}>{ht}'</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}
        </AccordionSection>

        {/* ADDITIONAL OPTIONS Section */}
        <AccordionSection
          title="ADDITIONAL OPTIONS"
          isOpen={openSections.frameSiding}
          onToggle={() => toggleSection('frameSiding')}
        >
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Select the materials and options for your frame and siding panels.
          </p>
          
          {/* Siding Panels */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Siding Panels</label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="sidingOrientation"
                  value="horizontal"
                  checked={config.sidingOrientation === 'horizontal'}
                  onChange={() => onConfigChange('sidingOrientation', 'horizontal')}
                  className="w-4 h-4 text-red-600 border-gray-300 dark:border-gray-600 focus:ring-red-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Horizontal</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="sidingOrientation"
                  value="vertical"
                  checked={config.sidingOrientation === 'vertical'}
                  onChange={() => onConfigChange('sidingOrientation', 'vertical')}
                  className="w-4 h-4 text-red-600 border-gray-300 dark:border-gray-600 focus:ring-red-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Vertical</span>
              </label>
            </div>
          </div>

          {/* Roof Rib Direction */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Roof Rib Direction</label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="roofRibDirection"
                  value="vertical"
                  checked={config.roofRibDirection === 'vertical' || !config.roofRibDirection}
                  onChange={() => onConfigChange('roofRibDirection', 'vertical')}
                  className="w-4 h-4 text-red-600 border-gray-300 dark:border-gray-600 focus:ring-red-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Vertical (Down Slope)</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="roofRibDirection"
                  value="horizontal"
                  checked={config.roofRibDirection === 'horizontal'}
                  onChange={() => onConfigChange('roofRibDirection', 'horizontal')}
                  className="w-4 h-4 text-red-600 border-gray-300 dark:border-gray-600 focus:ring-red-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Horizontal (Along Ridge)</span>
              </label>
            </div>
          </div>

          {/* Asphalt Anchors */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Asphalt Anchors</label>
            <select
              value={config.asphaltAnchors || 0}
              onChange={(e) => onConfigChange('asphaltAnchors', parseInt(e.target.value))}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value={0}>None</option>
              {[...Array(20)].map((_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </div>

          {/* Concrete Anchors */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Concrete Anchors</label>
            <select
              value={config.concreteAnchors || 0}
              onChange={(e) => onConfigChange('concreteAnchors', parseInt(e.target.value))}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value={0}>None</option>
              {[...Array(20)].map((_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </div>

          {/* Mobile Anchors */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Mobile Anchors</label>
            <select
              value={config.mobileAnchors || 0}
              onChange={(e) => onConfigChange('mobileAnchors', parseInt(e.target.value))}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value={0}>None</option>
              {[...Array(20)].map((_, i) => (
                <option key={i + 1} value={i + 1}>{i + 1}</option>
              ))}
            </select>
          </div>
        </AccordionSection>

        {/* DOORS Section */}
        <AccordionSection
          title="DOORS"
          isOpen={openSections.doors}
          onToggle={() => toggleSection('doors')}
          badge={config.garageDoors?.length + config.walkDoors?.length}
        >
          {enclosedWalls.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              Enclose at least one wall to add doors.
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Select a wall, then add doors. Drag the slider to position.
              </p>
              
              {/* Wall selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Select Wall</label>
                <select
                  value={selectedWall}
                  onChange={(e) => setSelectedWall(e.target.value)}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  {enclosedWalls.map(wall => (
                    <option key={wall.id} value={wall.id}>{wall.name}</option>
                  ))}
                </select>
              </div>

              {/* Garage Doors */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Garage / Roll-Up Doors</label>
                <div className="space-y-2">
                  {garageDoorOptions.map((door) => (
                    <button
                      key={door.id}
                      onClick={() => onAddGarageDoor(door.id, selectedWall)}
                      className="w-full p-3 text-left text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 flex items-center justify-between"
                    >
                      <span>{door.name}</span>
                      <span className="text-red-600 font-medium">+ Add</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Walk-In Door */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Walk-In Door</label>
                <button
                  onClick={() => onAddWalkDoor(selectedWall)}
                  className="w-full p-3 text-left text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 flex items-center justify-between"
                >
                  <span>Walk-In Door 3' x 6.5'</span>
                  <span className="text-red-600 font-medium">+ Add</span>
                </button>
              </div>

              {/* Added doors list */}
              {(config.garageDoors?.length > 0 || config.walkDoors?.length > 0) && (
                <div className="mt-6 border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Added Doors</label>
                  <div className="space-y-3">
                    {config.garageDoors?.map((door) => (
                      <div key={door.id} className="bg-gray-50 dark:bg-gray-800 p-3 rounded">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{door.type} - {enclosedWalls.find(w => w.id === door.wall)?.name}</span>
                          <button 
                            onClick={() => onRemoveItem('garageDoors', door.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <Move className="w-4 h-4 text-gray-400" />
                          <input
                            type="range"
                            min="10"
                            max="90"
                            value={door.position}
                            onChange={(e) => onUpdateItemPosition('garageDoors', door.id, parseInt(e.target.value))}
                            className="flex-1"
                          />
                          <span className="text-xs text-gray-500 w-8">{door.position}%</span>
                        </div>
                      </div>
                    ))}
                    {config.walkDoors?.map((door) => (
                      <div key={door.id} className="bg-gray-50 dark:bg-gray-800 p-3 rounded">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">Walk-In Door - {enclosedWalls.find(w => w.id === door.wall)?.name}</span>
                          <button 
                            onClick={() => onRemoveItem('walkDoors', door.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <Move className="w-4 h-4 text-gray-400" />
                          <input
                            type="range"
                            min="10"
                            max="90"
                            value={door.position}
                            onChange={(e) => onUpdateItemPosition('walkDoors', door.id, parseInt(e.target.value))}
                            className="flex-1"
                          />
                          <span className="text-xs text-gray-500 w-8">{door.position}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </AccordionSection>

        {/* WINDOWS Section */}
        <AccordionSection
          title="WINDOWS"
          isOpen={openSections.windows}
          onToggle={() => toggleSection('windows')}
          badge={config.windows?.length}
        >
          {enclosedWalls.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              Enclose at least one wall to add windows.
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Select a wall, then add windows. Use the slider to position.
              </p>
              
              {/* Wall selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Select Wall</label>
                <select
                  value={selectedWall}
                  onChange={(e) => setSelectedWall(e.target.value)}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  {enclosedWalls.map(wall => (
                    <option key={wall.id} value={wall.id}>{wall.name}</option>
                  ))}
                </select>
              </div>

              {/* Window options */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Window Sizes</label>
                <div className="space-y-2">
                  {windowOptions.map((window) => (
                    <button
                      key={window.id}
                      onClick={() => onAddWindow(window.id, selectedWall)}
                      className="w-full p-3 text-left text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between text-gray-900 dark:text-white"
                    >
                      <span>{window.name}</span>
                      <span className="text-red-600 font-medium">+ Add</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Added windows list */}
              {config.windows?.length > 0 && (
                <div className="mt-6 border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Added Windows</label>
                  <div className="space-y-3">
                    {config.windows?.map((window) => (
                      <div key={window.id} className="bg-gray-50 dark:bg-gray-800 p-3 rounded">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{window.type} - {enclosedWalls.find(w => w.id === window.wall)?.name}</span>
                          <button 
                            onClick={() => onRemoveItem('windows', window.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <Move className="w-4 h-4 text-gray-400" />
                          <input
                            type="range"
                            min="10"
                            max="90"
                            value={window.position}
                            onChange={(e) => onUpdateItemPosition('windows', window.id, parseInt(e.target.value))}
                            className="flex-1"
                          />
                          <span className="text-xs text-gray-500 w-8">{window.position}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </AccordionSection>

        {/* SERVICES, FINANCING, & WARRANTY Section */}
        <AccordionSection
          title="SERVICES, FINANCING, & WARRANTY"
          isOpen={openSections.services}
          onToggle={() => toggleSection('services')}
        >
          <div className="space-y-4">
            <h4 className="font-bold text-gray-800 dark:text-gray-200 text-sm border-b pb-2">IMPORTANT INFORMATION</h4>
            
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Services</p>
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.services?.buildOverExisting || false}
                  onChange={(e) => onConfigChange('services', { ...config.services, buildOverExisting: e.target.checked })}
                  className="w-4 h-4 mt-0.5 text-red-600 border-gray-300 dark:border-gray-600 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Build over existing structure</span>
              </label>
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.services?.cutLegsOnSite || false}
                  onChange={(e) => onConfigChange('services', { ...config.services, cutLegsOnSite: e.target.checked })}
                  className="w-4 h-4 mt-0.5 text-red-600 border-gray-300 dark:border-gray-600 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Cut Legs on Site</span>
              </label>
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.services?.installOverExisting || false}
                  onChange={(e) => onConfigChange('services', { ...config.services, installOverExisting: e.target.checked })}
                  className="w-4 h-4 mt-0.5 text-red-600 border-gray-300 dark:border-gray-600 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Install over existing structure <span className="text-red-500">(Fees Apply)</span></span>
              </label>
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.services?.installOnDeck || false}
                  onChange={(e) => onConfigChange('services', { ...config.services, installOnDeck: e.target.checked })}
                  className="w-4 h-4 mt-0.5 text-red-600 border-gray-300 dark:border-gray-600 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Install on Deck or Dock (Non-Floating)</span>
              </label>
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.services?.installOnWall || false}
                  onChange={(e) => onConfigChange('services', { ...config.services, installOnWall: e.target.checked })}
                  className="w-4 h-4 mt-0.5 text-red-600 border-gray-300 dark:border-gray-600 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Install on Wall (Up to 3')</span>
              </label>
            </div>
          </div>
        </AccordionSection>

        {/* STRUCTURE DETAILS Section */}
        <AccordionSection
          title="STRUCTURE DETAILS"
          isOpen={openSections.structureDetails}
          onToggle={() => toggleSection('structureDetails')}
        >
          <div className="space-y-3 text-sm">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Style:</span>
                <span className="font-medium text-gray-800 dark:text-white">{currentStyle?.name || 'Carport'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Base Size:</span>
                <span className="font-medium text-gray-800 dark:text-white">{config.width}'x{config.length}'</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Installation Surface:</span>
                <span className="font-medium text-gray-800 dark:text-white capitalize">{config.installationSurface}</span>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
              <h5 className="font-semibold text-gray-700 dark:text-gray-200 border-b pb-1 mb-2">Colors</h5>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300">Roof:</span>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: config.roofColor }}></div>
                  <span className="font-medium text-gray-800 dark:text-white">{getColorName(config.roofColor)}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300">Trim:</span>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: config.trimColor }}></div>
                  <span className="font-medium text-gray-800 dark:text-white">{getColorName(config.trimColor)}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300">Side/Wall:</span>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: config.sideColor }}></div>
                  <span className="font-medium text-gray-800 dark:text-white">{getColorName(config.sideColor)}</span>
                </div>
              </div>
              {config.wainscotColor && config.wainscotColor !== 'none' && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-300">Wainscot (36"):</span>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: config.wainscotColor }}></div>
                    <span className="font-medium text-gray-800 dark:text-white">{getColorName(config.wainscotColor)}</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
              <h5 className="font-semibold text-gray-700 dark:text-gray-200 border-b pb-1 mb-2">Roof Details</h5>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Roof Style:</span>
                <span className="font-medium text-gray-800 dark:text-white">{roofStyles.find(r => r.id === config.roofStyle)?.name || 'Regular'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Roof Pitch:</span>
                <span className="font-medium text-gray-800 dark:text-white">{config.roofPitch}/12</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Roof Overhang:</span>
                <span className="font-medium text-gray-800 dark:text-white">6"</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Trusses:</span>
                <span className="font-medium text-gray-800 dark:text-white">Standard</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Brace:</span>
                <span className="font-medium text-gray-800 dark:text-white">Standard Brace</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Leg Height:</span>
                <span className="font-medium text-gray-800 dark:text-white">{config.legHeight}'</span>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
              <h5 className="font-semibold text-gray-700 dark:text-gray-200 border-b pb-1 mb-2">Enclosures</h5>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Left Side:</span>
                <span className="font-medium text-gray-800 dark:text-white capitalize">
                  {config.frontSide === 'enclosed' ? `Fully Enclosed - Siding: ${config.sidingOrientation === 'horizontal' ? 'Horizontal' : 'Vertical'}` : config.frontSide === 'partial' ? 'Partially Enclosed' : 'Open'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Right Side:</span>
                <span className="font-medium text-gray-800 dark:text-white capitalize">
                  {config.backSide === 'enclosed' ? `Fully Enclosed - Siding: ${config.sidingOrientation === 'horizontal' ? 'Horizontal' : 'Vertical'}` : config.backSide === 'partial' ? 'Partially Enclosed' : 'Open'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Front End:</span>
                <span className="font-medium text-gray-800 dark:text-white capitalize">
                  {config.leftEnd === 'enclosed' ? `Fully Enclosed - Siding: ${config.sidingOrientation === 'horizontal' ? 'Horizontal' : 'Vertical'}` : config.leftEnd === 'partial' ? 'Partially Enclosed' : 'Open'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">Back End:</span>
                <span className="font-medium text-gray-800 dark:text-white capitalize">
                  {config.rightEnd === 'enclosed' ? `Fully Enclosed - Siding: ${config.sidingOrientation === 'horizontal' ? 'Horizontal' : 'Vertical'}` : config.rightEnd === 'partial' ? 'Partially Enclosed' : 'Open'}
                </span>
              </div>
            </div>
            
            {/* Doors & Windows */}
            {(config.walkDoors?.length > 0 || config.garageDoors?.length > 0 || config.windows?.length > 0) && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                <h5 className="font-semibold text-gray-700 dark:text-gray-200 border-b pb-1 mb-2">Doors & Windows</h5>
                {config.walkDoors?.map((door, idx) => (
                  <div key={door.id} className="text-gray-800 dark:text-white">Walk-in Door (36x80)</div>
                ))}
                {config.garageDoors?.length > 0 && (
                  <div className="text-gray-800 dark:text-white">
                    {config.garageDoors[0].width}x{config.garageDoors[0].height} Rollup Door
                    {config.garageDoors.length > 1 && <span className="text-gray-500 dark:text-gray-400"> (Qty. {config.garageDoors.length})</span>}
                  </div>
                )}
                {config.windows?.map((win, idx) => (
                  <div key={win.id} className="text-gray-800 dark:text-white">Window {win.width}'x{win.height}'</div>
                ))}
              </div>
            )}
            
            {/* Services if selected */}
            {config.services && Object.values(config.services).some(v => v) && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                <h5 className="font-semibold text-gray-700 dark:text-gray-200 border-b pb-1 mb-2">Selected Services</h5>
                {config.services.buildOverExisting && <div className="text-gray-800 dark:text-white">• Build over existing structure</div>}
                {config.services.cutLegsOnSite && <div className="text-gray-800 dark:text-white">• Cut Legs on Site</div>}
                {config.services.installOverExisting && <div className="text-gray-800 dark:text-white">• Install over existing structure</div>}
                {config.services.installOnDeck && <div className="text-gray-800 dark:text-white">• Install on Deck or Dock</div>}
                {config.services.installOnWall && <div className="text-gray-800 dark:text-white">• Install on Wall (Up to 3')</div>}
              </div>
            )}
            
            {/* Notes */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-1">
              <p className="text-xs text-yellow-800"><strong>Note:</strong> Ground must be level.</p>
              <p className="text-xs text-yellow-800"><strong>Note:</strong> There is a 25% Restocking Fee on all Cancelled orders.</p>
            </div>
          </div>
        </AccordionSection>

        {/* BUILDING ESTIMATE Section - Only visible to salespeople */}
        {canSeePricing && buildingEstimate && (
          <AccordionSection
            title="BUILDING ESTIMATE"
            isOpen={openSections.buildingEstimate}
            onToggle={() => toggleSection('buildingEstimate')}
          >
            <div className="space-y-3" data-testid="building-estimate-section">
              {/* Main Estimate */}
              <div className="bg-white border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 dark:text-gray-200 font-medium">Building Estimate:</span>
                  <span className="text-lg font-bold text-gray-800">${buildingEstimate.buildingEstimate?.toLocaleString()}</span>
                </div>
                
                <div className="flex justify-between items-center text-green-600">
                  <span className="font-medium">MANUFACTURER DISCOUNT ({buildingEstimate.discountPercent}%):</span>
                  <span className="font-bold">-${buildingEstimate.discountAmount?.toLocaleString()}</span>
                </div>
                
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 dark:text-gray-200 font-medium">Subtotal:</span>
                    <span className="font-bold text-gray-800">${buildingEstimate.subtotal?.toLocaleString()}</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center text-blue-600">
                  <span className="font-medium">Sales Tax ({buildingEstimate.taxPercent}%):</span>
                  <span className="font-bold">${buildingEstimate.taxAmount?.toLocaleString()}</span>
                </div>
                
                <div className="border-t pt-3 bg-red-50 -mx-4 px-4 py-3 rounded-b-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-[#C41E3A] font-bold text-lg">Total:</span>
                    <span className="text-2xl font-bold text-[#C41E3A]">${buildingEstimate.total?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              
              {/* Payment Breakdown */}
              <div className="bg-gray-50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-2">
                <h5 className="font-semibold text-gray-700 dark:text-gray-200 border-b pb-2 mb-2">Payment Breakdown</h5>
                <div className="flex justify-between items-center text-purple-700">
                  <span className="font-medium">Deposit Amount ({buildingEstimate.depositPercent}%):</span>
                  <span className="font-bold">${buildingEstimate.depositAmount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-300 font-medium">Due Upon Delivery:</span>
                  <span className="font-bold text-gray-800">${buildingEstimate.dueOnDelivery?.toLocaleString()}</span>
                </div>
              </div>
              
              {/* Info Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-700">
                  <strong>Note:</strong> This estimate is for sales reference only. Final pricing may vary based on installation requirements and site conditions.
                </p>
              </div>
            </div>
          </AccordionSection>
        )}
      </div>

      {/* Bottom action - fixed */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
        <button className="w-full py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold rounded hover:bg-gray-200 transition-colors flex items-center justify-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
          </svg>
          Save Design
        </button>
      </div>
    </div>
  );
};

// Style icons for each building type
const StyleIcon = ({ styleId, isSelected }) => {
  const color = isSelected ? '#C41E3A' : '#6B7280';
  
  const icons = {
    carport: (
      <svg className="w-12 h-10" viewBox="0 0 60 40">
        <polygon points="30,5 55,18 5,18" fill={color} opacity="0.5" />
        <line x1="8" y1="18" x2="8" y2="35" stroke={color} strokeWidth="2" />
        <line x1="52" y1="18" x2="52" y2="35" stroke={color} strokeWidth="2" />
        <line x1="5" y1="35" x2="55" y2="35" stroke={color} strokeWidth="1" />
      </svg>
    ),
    carport_30: (
      <svg className="w-12 h-10" viewBox="0 0 60 40">
        <polygon points="30,3 58,16 2,16" fill={color} opacity="0.5" />
        <line x1="6" y1="16" x2="6" y2="35" stroke={color} strokeWidth="2" />
        <line x1="30" y1="16" x2="30" y2="35" stroke={color} strokeWidth="2" />
        <line x1="54" y1="16" x2="54" y2="35" stroke={color} strokeWidth="2" />
        <line x1="2" y1="35" x2="58" y2="35" stroke={color} strokeWidth="1" />
      </svg>
    ),
    carport_40_50: (
      <svg className="w-12 h-10" viewBox="0 0 60 40">
        <polygon points="30,2 59,14 1,14" fill={color} opacity="0.5" />
        <line x1="4" y1="14" x2="4" y2="35" stroke={color} strokeWidth="2" />
        <line x1="20" y1="14" x2="20" y2="35" stroke={color} strokeWidth="2" />
        <line x1="40" y1="14" x2="40" y2="35" stroke={color} strokeWidth="2" />
        <line x1="56" y1="14" x2="56" y2="35" stroke={color} strokeWidth="2" />
        <line x1="1" y1="35" x2="59" y2="35" stroke={color} strokeWidth="1" />
      </svg>
    ),
    rv_cover: (
      <svg className="w-12 h-10" viewBox="0 0 60 40">
        <polygon points="30,2 52,12 8,12" fill={color} opacity="0.5" />
        <line x1="10" y1="12" x2="10" y2="38" stroke={color} strokeWidth="2" />
        <line x1="50" y1="12" x2="50" y2="38" stroke={color} strokeWidth="2" />
        <rect x="18" y="28" width="24" height="10" fill={color} opacity="0.2" />
        <line x1="5" y1="38" x2="55" y2="38" stroke={color} strokeWidth="1" />
      </svg>
    ),
    garage: (
      <svg className="w-12 h-10" viewBox="0 0 60 40">
        <polygon points="30,3 55,15 5,15" fill={color} opacity="0.5" />
        <rect x="5" y="15" width="50" height="22" fill={color} opacity="0.3" />
        <rect x="15" y="22" width="15" height="15" fill={color} opacity="0.5" />
        <line x1="5" y1="37" x2="55" y2="37" stroke={color} strokeWidth="1" />
      </svg>
    ),
    side_bay_garage: (
      <svg className="w-12 h-10" viewBox="0 0 60 40">
        <polygon points="30,3 55,15 5,15" fill={color} opacity="0.5" />
        <rect x="5" y="15" width="30" height="22" fill={color} opacity="0.3" />
        <rect x="10" y="22" width="10" height="15" fill={color} opacity="0.5" />
        <line x1="35" y1="15" x2="35" y2="37" stroke={color} strokeWidth="2" />
        <line x1="50" y1="15" x2="50" y2="37" stroke={color} strokeWidth="2" />
        <line x1="5" y1="37" x2="55" y2="37" stroke={color} strokeWidth="1" />
      </svg>
    ),
    barn: (
      <svg className="w-12 h-10" viewBox="0 0 60 40">
        <polygon points="30,1 55,18 5,18" fill={color} opacity="0.5" />
        <rect x="5" y="18" width="50" height="19" fill={color} opacity="0.3" />
        <rect x="20" y="20" width="20" height="17" fill={color} opacity="0.5" />
        <line x1="30" y1="20" x2="30" y2="37" stroke={color} strokeWidth="1" />
        <line x1="5" y1="37" x2="55" y2="37" stroke={color} strokeWidth="1" />
      </svg>
    ),
    shop_port: (
      <svg className="w-12 h-10" viewBox="0 0 60 40">
        <polygon points="30,5 55,17 5,17" fill={color} opacity="0.5" />
        <rect x="25" y="17" width="30" height="20" fill={color} opacity="0.3" />
        <line x1="8" y1="17" x2="8" y2="37" stroke={color} strokeWidth="2" />
        <line x1="25" y1="17" x2="25" y2="37" stroke={color} strokeWidth="2" />
        <line x1="5" y1="37" x2="55" y2="37" stroke={color} strokeWidth="1" />
      </svg>
    ),
    single_slope: (
      <svg className="w-12 h-10" viewBox="0 0 60 40">
        <polygon points="5,8 55,18 55,18 5,18" fill={color} opacity="0.5" />
        <line x1="8" y1="10" x2="8" y2="35" stroke={color} strokeWidth="2" />
        <line x1="52" y1="18" x2="52" y2="35" stroke={color} strokeWidth="2" />
        <line x1="5" y1="35" x2="55" y2="35" stroke={color} strokeWidth="1" />
      </svg>
    ),
  };

  return icons[styleId] || icons.carport;
};

export default ConfiguratorSidebar;
